import { createSelector } from '@reduxjs/toolkit';
import { chartOptions } from '@/constant/chart/chart';
import { RootState } from '../types';

// ============================ Loading Selector ============================

export const getDashboardLoading = (state: RootState) => {
  return (
    state?.dashboard?.loading ?? {
      vdiTransactions: false,
      vdiByType: { mmsDevices: false, mmsMarkets: false, mmsProducts: false, mmsSales: false },
      smartCoolerProducts: false,
      alerts: false,
      quikPickProducts: false,
    }
  );
};

// ============================ Dashboard Data ============================

const getDashboardData = function (state: RootState) {
  return state?.dashboard?.dashboard;
};

export const getVdiTransactionCount = createSelector(
  getDashboardData,
  (dashboard) => {
    // Sum `requestReceived` as the primary volume metric.
    // Falls back to legacy `accepted` or `total` when absent.
    const sumReceived = (transactions: any[]): number =>
      (transactions ?? []).reduce((sum: number, item: any) => {
        const v = item?.requestReceived ?? item?.accepted ?? item?.total;
        return sum + (parseInt(v) || 0);
      }, 0);

    return Object.values(dashboard?.data?.vdiTransactions ?? {}).reduce(
      (total: number, cats: any) => total + sumReceived(cats),
      0,
    );
  },
);

/**
 * Parse the first date part from any supported `range` format and return
 * a comparable UTC millisecond timestamp for sorting.
 *
 *   "2026-07-01"                   → day groupBy (YYYY-MM-DD)
 *   "2026-06-27 - 2026-07-03"      → week groupBy (hyphen separator)
 *   "2026-06-27 \u2013 2026-07-03"      → week groupBy (en-dash separator)
 *   "2026-06"                      → month groupBy
 *   "2026"                         → year groupBy
 */
const parseDateMs = (raw: string | undefined | null): number => {
  if (!raw) return 0;
  // Strip week range suffix — handle both " - " (hyphen) and " \u2013 " (en-dash)
  const hyphen = raw.indexOf(' - ');
  const endash = raw.indexOf(' \u2013 ');
  const sepIdx = hyphen !== -1 ? hyphen : endash !== -1 ? endash : -1;
  const first = sepIdx !== -1 ? raw.slice(0, sepIdx).trim() : raw.trim();

  // YYYY-MM-DD or ISO with time component
  if (/^\d{4}-\d{2}-\d{2}/.test(first)) {
    const d = new Date(first);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  // Month only: YYYY-MM → first of that month (UTC)
  const mm = first.match(/^(\d{4})-(\d{2})$/);
  if (mm) return Date.UTC(Number(mm[1]), Number(mm[2]) - 1, 1);
  // Year only: YYYY → Jan 1 of that year (UTC)
  if (/^\d{4}$/.test(first)) return Date.UTC(Number(first), 0, 1);

  return 0;
};

/** Sort raw API items ascending by their `range` or `date` field. */
const sortByDate = <T extends { range?: string; date?: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => parseDateMs(a.range ?? a.date) - parseDateMs(b.range ?? b.date));

export const getModifiedDashboarddata = createSelector(
  getDashboardData,
  (dashboard) => {
    const finalVdiTransactions: Record<string, any> = {};
    const vdiTransactionsType: string[] = [];

    for (const key of Object.keys(dashboard?.data?.vdiTransactions ?? {})) {
      const sortedVdi = sortByDate(dashboard?.data?.vdiTransactions[key] ?? []);

      // Always include every type so DashboardChart can render its own no-data state
      vdiTransactionsType.push(key);

      // ── Series ordered for grouped-stacked layout ─────────────────────────────
      // Group 0 (Inbound / Success):  [0] Successful (bottom)  [1] In Process (top)
      // Group 1 (Failures):           [2] Process Failed  [3] Partner Failed
      // requestFailed = total failure count (stat card only, NOT a chart series)
      const seriesSuccess = { name: 'Successful', data: [] as number[] };
      const seriesInProcess = { name: 'In Process', data: [] as number[] };
      const seriesProcFailed = { name: 'Process Failed', data: [] as number[] };
      const seriesPartnerFailed = { name: 'Partner Failed', data: [] as number[] };

      sortedVdi.forEach((item: any) => {
        seriesSuccess.data.push(
          parseInt(item?.requestSuccess) || parseInt(item?.accepted) || parseInt(item?.processed) || 0,
        );
        seriesInProcess.data.push(parseInt(item?.requestInProcess) || 0);
        seriesProcFailed.data.push(parseInt(item?.processFailed) || 0);
        seriesPartnerFailed.data.push(parseInt(item?.partnerFailed) || 0);
      });

      finalVdiTransactions[key] = {
        series: [seriesSuccess, seriesInProcess, seriesProcFailed, seriesPartnerFailed],
        options: chartOptions(sortedVdi),
      };
    }
    // ── 5 alert series (grouped-stacked layout) ─────────────────────────────
    // Group 0 (Activity):  [0] Closed (bottom)  [1] Open (top)
    // Group 1 (Failures):  [2] Process Failed   [3] Partner Failed
    // receivedAlerts = total inbound count (stat card only, NOT a chart series)
    // failedAlerts   = total failure count (stat card only, NOT a chart series)
    const sortedAlerts = sortByDate(dashboard?.data?.alerts ?? []);

    const seriesClosed = { name: 'Closed', data: [] as number[] };
    const seriesOpen = { name: 'Open', data: [] as number[] };
    const seriesPartnerFailed = { name: 'Partner Failed', data: [] as number[] };
    const seriesProcFailed = { name: 'Process Failed', data: [] as number[] };

    sortedAlerts.forEach((item: any) => {
      seriesClosed.data.push(parseInt(item?.closedAlerts) || 0);
      seriesOpen.data.push(parseInt(item?.openAlerts) || 0);
      seriesPartnerFailed.data.push(parseInt(item?.partnerFailedAlerts) || 0);
      seriesProcFailed.data.push(parseInt(item?.processFailedAlerts) || 0);
    });

    // Always create the chart object — DashboardChart handles the no-data empty state
    const finalAlertData = {
      series: [seriesClosed, seriesOpen, seriesProcFailed, seriesPartnerFailed],
      options: {
        ...chartOptions(sortedAlerts),
        tooltipTotals: sortedAlerts.map((item: any) => parseInt(item?.receivedAlerts) || 0),
      },
    };

    return {
      ...dashboard,

      data: {
        ...dashboard?.data,
        vdiTransactions: finalVdiTransactions,
        alerts: finalAlertData,
        type: vdiTransactionsType,
      },
    };
  },
);
